import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { IMAGES, BRAND } from "../../data/content";
import { Reveal } from "./Reveal";
import { BrandName } from "./BrandName";

const CHAPTERS = [
  { no: "01", title: "Fresh to order", body: "Nothing sits under a heat lamp. Every burger is smashed, every philly is seared and every plate is built the moment you order it." },
  { no: "02", title: "100% Halal, always", body: "Our entire menu is 100% Halal — no exceptions. Quality meat, honest ingredients, food the whole family can trust." },
  { no: "03", title: "Open every day", body: "Open seven days a week, late into the night. Whenever the craving hits, we're firing up the grill fresh for you." },
];

export const Story = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", reduce ? "-8%" : "12%"]);

  return (
    <section ref={ref} className="relative border-y border-white/8 bg-surface overflow-hidden grain">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Why Winnipeg loves us</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
              Not your average <br />
              <BrandName className="text-4xl sm:text-5xl lg:text-6xl" /> joint.
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-6 max-w-lg text-sm sm:text-base text-cream/60 leading-relaxed">
              {BRAND.description} <span className="text-gold-gloss font-display font-semibold">{BRAND.kicker}</span>
            </p>
          </Reveal>

          <div className="mt-12 space-y-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08}>
                <div className="flex gap-5 border-t border-white/10 pt-6">
                  <span className="font-display text-lg font-semibold text-gold/70">{c.no}</span>
                  <div>
                    <h3 className="font-display text-2xl font-semibold text-cream">{c.title}</h3>
                    <p className="mt-2 max-w-md text-sm sm:text-base text-cream/60 leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="relative">
          <Reveal className="h-full">
            <div className="relative h-[380px] sm:h-[520px] lg:h-full overflow-hidden rounded-3xl border border-white/10">
              <motion.img
                style={{ y }}
                src={IMAGES.meatStack}
                alt="Smoky Meat Stack"
                className="absolute inset-0 h-[125%] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
              <div className="absolute bottom-6 left-6 rounded-2xl glass border border-gold/20 px-5 py-4">
                <p className="font-display text-3xl font-semibold text-gold">4.9★</p>
                <p className="text-xs text-cream/70">Loved across Winnipeg</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Story;
